import {
  BlankSlate,
  Box,
  Link,
  Skeleton,
  Table,
  Title,
} from '@factorialco/deprecated-design-system'
import { GetCampaignJobPostingsDocument } from 'generated/resources/graphql'
import { isEmpty, map, sample } from 'lodash'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { VonqCampaignsTable } from 'modules/ats/components/VonqCampaignsTable'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

import { useGetCampaignsGroupedByJob } from './useGetCampaignsGroupedByJob'

const JobsCampaignsPage = () => {
  const { navigation } = useNavigation()
  const { loading, campaigns } = useGetCampaignsGroupedByJob()
  const { data } = useQuery(GetCampaignJobPostingsDocument, {
    variables: { ids: Object.keys(campaigns).map((id) => Number(id)) },
    skip: loading,
  })

  const jobs = data?.ats.jobPostings
  const showSkeleton = loading || !data

  const noCampaigns = isEmpty(campaigns)

  return (
    <ResponsivePage.Body variant="full" showHeader={false}>
      <Box width="full" paddingX="s48" paddingTop="s12" gap="s32">
        {showSkeleton ? (
          <SkeletonContent />
        ) : noCampaigns ? (
          <BlankSlate
            illustration="document-folder-drawer-2"
            title={i18n.t('ats.vonq.campaigns_list.empty.title')}
            description={i18n.t('ats.vonq.campaigns_list.empty.description')}
          >
            <Link.Router to={navigation.ats.jobs.fullPath()}>
              {i18n.t('ats.vonq.campaigns_list.empty.link')}
            </Link.Router>
          </BlankSlate>
        ) : (
          map(campaigns, (products, jobId) => {
            const job = jobs?.find((job) => job.id === Number(jobId))

            if (!job) {
              return null
            }

            const jobLocation = job.location?.name
              ? i18n.t('ats.vonq.campaigns_list.location', { jobLocation: job.location?.name })
              : ''

            return (
              <Box key={jobId} gap="s24">
                <Title variant="500" title={`${job.title} ${jobLocation}`} />

                <VonqCampaignsTable campaigns={products} />
              </Box>
            )
          })
        )}
      </Box>
    </ResponsivePage.Body>
  )
}

const SkeletonContent = () => {
  return (
    <Box gap="s32">
      {Array.from({ length: 3 }).map((_, index) => {
        return (
          <Box key={index} gap="s16">
            <Skeleton isLoading width="s300" height="s12" />

            <Table>
              <Table noRadius hideBorder>
                <Table.Head>
                  <Table.Row>
                    <Table.HeadCell colSpan={3}>
                      <Skeleton isLoading width="s120" />
                    </Table.HeadCell>
                    <Table.HeadCell>
                      <Skeleton isLoading width="s120" />
                    </Table.HeadCell>
                    <Table.HeadCell>
                      <Skeleton isLoading width="s120" />
                    </Table.HeadCell>
                  </Table.Row>
                </Table.Head>
              </Table>

              <Table noRadius hideBorder>
                <Table.Body>
                  <Table.Row>
                    <Table.Cell colSpan={3}>
                      <Skeleton isLoading width={sample(['s300', 's400', 's200'])} />
                    </Table.Cell>
                    <Table.Cell>
                      <Skeleton isLoading />
                    </Table.Cell>
                    <Table.Cell>
                      <Skeleton isLoading />
                    </Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell colSpan={3}>
                      <Skeleton isLoading width={sample(['s300', 's400', 's200'])} />
                    </Table.Cell>
                    <Table.Cell>
                      <Skeleton isLoading />
                    </Table.Cell>
                    <Table.Cell>
                      <Skeleton isLoading />
                    </Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell colSpan={3}>
                      <Skeleton isLoading width={sample(['s300', 's400', 's200'])} />
                    </Table.Cell>
                    <Table.Cell>
                      <Skeleton isLoading />
                    </Table.Cell>
                    <Table.Cell>
                      <Skeleton isLoading />
                    </Table.Cell>
                  </Table.Row>
                </Table.Body>
              </Table>
            </Table>
          </Box>
        )
      })}
    </Box>
  )
}

export default JobsCampaignsPage
